import React, { useRef, useState } from "react";
import {
  Box,
  Typography,
  makeStyles,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
} from "@material-ui/core";
import { useTranslation } from "react-i18next";
import AppButton from "components/CustomButtons/AppButton";
import AppColor from "utilities/AppColor";
import UploadDocumentModal from "../form/UploadDocumentModal";
import DocumentVersionsModal from "../form/DocumentVersionsModal";
import VisibilityIcon from '@material-ui/icons/Visibility';
import { IconButton, Tooltip } from "@material-ui/core";
import moment from "moment";
import { CLIENT_DOCUMENT_STATUS } from "config/AppConfig";

const useStyles = makeStyles(() => ({
  sectionHeader: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    marginBottom: 10,
  },
  sectionTitle: {
    fontSize: 13,
    fontWeight: 700,
    color: "#0D9488",
    textTransform: "uppercase",
    letterSpacing: "0.05em",
  },
  table: {
    width: "100%",
    borderCollapse: "collapse",
    fontSize: 12,
    marginBottom: 24,
  },
  tableHead: {
    background: "#FAFAFA",
  },
  tableCellHead: {
    padding: "10px 12px",
    textAlign: "left",
    fontWeight: 600,
    color: "#6B7280",
    borderBottom: "1px solid #E5E7EB",
    fontSize: 11,
  },
  tableCellBody: {
    padding: "12px",
    borderBottom: "1px solid #F3F4F6",
    color: "#111827",
    fontSize: 12,
    verticalAlign: "middle",
  },
  pill: {
    padding: "2px 10px",
    borderRadius: 12,
    fontSize: 11,
    fontWeight: 600,
    display: "inline-block",
    border: "1px solid transparent",
  },
  emptyRow: {
    textAlign: "center",
    color: "#9CA3AF",
    padding: "28px 0",
    fontSize: 13,
  },
  actionsRow: {
    display: "flex",
    gap: "8px",
    alignItems: "center",
  },
  actionBtn: {
    fontSize: "11px",
    padding: "4px 12px",
    borderRadius: "20px",
    cursor: "pointer",
    border: "1px solid",
    background: "transparent",
    fontWeight: 500,
    textDecoration: "none",
  },
}));

const STATUS_STYLES = {
  [CLIENT_DOCUMENT_STATUS.PENDING]: { bg: "#FFE4E6", color: "#BE123C" },
  [CLIENT_DOCUMENT_STATUS.CURRENT]: { bg: "#CCFBF1", color: "#0D9488" },
  [CLIENT_DOCUMENT_STATUS.SIGNED]:  { bg: "#CCFBF1", color: "#0D9488" },
  [CLIENT_DOCUMENT_STATUS.REVIEW_DUE]: { bg: "#FFEDD5", color: "#9A3412" },
  [CLIENT_DOCUMENT_STATUS.OVERDUE]: { bg: "#FEE2E2", color: "#991B1B" },
  [CLIENT_DOCUMENT_STATUS.SUPERSEDED]: { bg: "#F3F4F6", color: "#6B7280" },
};

const DocumentClientTab = ({ client, reloadClientDetail }) => {
  const classes = useStyles();
  const { t } = useTranslation();

  const [showUploadModal, setShowUploadModal] = useState(false);
  const [editDocData, setEditDocData] = useState(null);
  const [showVersionsModal, setShowVersionsModal] = useState(false);
  const [versionsData, setVersionsData] = useState([]);
  const [versionsTitle, setVersionsTitle] = useState("");
  const [uploadFile, setUploadFile] = useState(null);

  const hiddenFileInputRef = useRef(null);

  const documents = client?.clientDocumentResponses || [];

  const handleUpload = () => {
    if (hiddenFileInputRef.current) {
      hiddenFileInputRef.current.click();
    }
  };

  const handleFileSelect = (e) => {
    const file = e.target.files[0];
    if (file) {
      setUploadFile(file);
      const nameWithoutExtension = file.name.replace(/\.[^/.]+$/, "");
      setEditDocData({ fileName: nameWithoutExtension });
      setShowUploadModal(true);
    }
    e.target.value = null; // reset so same file can be chosen again
  };

  const handleEdit = (doc) => {
    setUploadFile(null);
    setEditDocData(doc);
    setShowUploadModal(true);
  };

  const handleView = (doc) => {
    // Show all versions. Backend returns newest first.
    const details = [...(doc.clientDocumentDetailResponse || [])];
    setVersionsData(details);
    setVersionsTitle(doc.fileName);
    setShowVersionsModal(true);
  };

  const renderStatusPill = (status) => {
    if (!status) return "-";
    const s = STATUS_STYLES[status] || { bg: "#F3F4F6", color: "#6B7280" };
    return (
      <span className={classes.pill} style={{ background: s.bg, color: s.color }}>
        {t(`documentStatus.${status}`)}
      </span>
    );
  };

  const formatDate = (d) => (d ? moment(d).format("D MMM YYYY") : "-");

  return (
    <Box>
      <Box className={classes.sectionHeader}>
        <Typography className={classes.sectionTitle}>{t("clientDocument.participantDocuments")}</Typography>
        <AppButton
          label={t("button.upload")}
          buttonColor={AppColor.mainColor}
          mode="outlined"
          rounder
          style={{ fontSize: 11, textTransform: "none" }}
          startIcon={<i className="fas fa-upload" style={{ fontSize: 10 }} />}
          onClick={handleUpload}
        />
      </Box>

      <Table className={classes.table}>
        <TableHead className={classes.tableHead}>
          <TableRow>
            <TableCell className={classes.tableCellHead} style={{ width: "25%" }}>{t("clientDocument.document")}</TableCell>
            <TableCell className={classes.tableCellHead} style={{ width: "15%" }}>{t("clientDocument.category")}</TableCell>
            <TableCell className={classes.tableCellHead} style={{ width: "15%" }}>{t("clientDocument.uploaded")}</TableCell>
            <TableCell className={classes.tableCellHead} style={{ width: "15%" }}>{t("clientDocument.reviewDue")}</TableCell>
            <TableCell className={classes.tableCellHead} style={{ width: "15%" }}>{t("clientDocument.status")}</TableCell>
            <TableCell className={classes.tableCellHead} style={{ width: "15%" }}>{t("clientDocument.actions")}</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {documents.length > 0 ? (
            documents.map((row, index) => {
              const sortedDetails = row.clientDocumentDetailResponse
                ? [...row.clientDocumentDetailResponse].sort(
                    (a, b) =>
                      new Date(b.createdDate).getTime() -
                      new Date(a.createdDate).getTime()
                  )
                : [];
              const latestDetail = sortedDetails[0] || {};
              
              let reviewDateColor = "inherit";
              let displayStatus = latestDetail.status;
              if (latestDetail.reviewDueDate) {
                const diffDays = moment(latestDetail.reviewDueDate).diff(moment().startOf('day'), 'days');
                if (diffDays <= 0) {
                  reviewDateColor = "#991B1B"; // Overdue color as fallback
                  displayStatus = CLIENT_DOCUMENT_STATUS.OVERDUE;
                } else if (diffDays <= 30) {
                  reviewDateColor = "#D97706";
                  displayStatus = CLIENT_DOCUMENT_STATUS.REVIEW_DUE;
                }
              }
              const reviewDueFormatted = latestDetail.reviewDueDate ? `${formatDate(latestDetail.reviewDueDate)}${displayStatus === CLIENT_DOCUMENT_STATUS.REVIEW_DUE ? ` - ${t('label.due')}` : ''}` : "-";

              return (
                <TableRow key={row.id || index}>
                  <TableCell className={classes.tableCellBody}>
                    <Box fontWeight={600}>{row.fileName}</Box>
                  </TableCell>
                  <TableCell className={classes.tableCellBody}>{row.fileCategory ? t(`documentCategory.${row.fileCategory}`) : "-"}</TableCell>
                  <TableCell className={classes.tableCellBody}>{formatDate(row.createdDate)}</TableCell>
                  <TableCell className={classes.tableCellBody}>
                    <span style={{ color: reviewDateColor, fontWeight: reviewDateColor !== "inherit" ? 600 : 400 }}>
                      {reviewDueFormatted}
                    </span>
                  </TableCell>
                  <TableCell className={classes.tableCellBody}>{renderStatusPill(displayStatus)}</TableCell>
                  <TableCell className={classes.tableCellBody}>
                    <Box className={classes.actionsRow}>
                      <Tooltip title={t("clientDocument.view") || "View"}>
                        <IconButton
                          size="small"
                          onClick={() => handleView(row)}
                          style={{ color: "#374151" }}
                        >
                          <VisibilityIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title={t("button.edit")}>
                        <IconButton
                          size="small"
                          onClick={() => handleEdit({ ...row, ...latestDetail, id: row.id })}
                          style={{ color: AppColor.mainColor }}
                        >
                          
                          <i className={`fas fa-edit ${classes.editIcon}`} />
                        </IconButton>
                      </Tooltip>
                    </Box>
                  </TableCell>
                </TableRow>
              );
            })
          ) : (
            <TableRow>
              <TableCell colSpan={6} className={classes.tableCellBody}>
                <Box className={classes.emptyRow}>{t("clientDocument.noDocuments")}</Box>
              </TableCell>
            </TableRow>
          )}
        </TableBody>
      </Table>

      <input
        ref={hiddenFileInputRef}
        type="file"
        accept="image/jpeg,image/png,image/jpg,application/pdf,.doc,.docx,application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document"
        style={{ display: "none" }}
        onChange={handleFileSelect}
      />

      <UploadDocumentModal
        open={showUploadModal}
        onClose={() => setShowUploadModal(false)}
        clientId={client?.id}
        onSuccess={reloadClientDetail}
        initialData={editDocData}
        initialFile={uploadFile}
      />

      <DocumentVersionsModal
        open={showVersionsModal}
        onClose={() => setShowVersionsModal(false)}
        details={versionsData}
        documentName={versionsTitle}
      />
    </Box>
  );
};

export default DocumentClientTab;
